/**
 * Agent Architect Page
 *
 * Visual drag-and-drop editor for building agent workflows
 * with LangGraph/Unified Execution hybrid backend integration
 */
import { useState, useCallback, useRef, useEffect } from 'react'
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  Background,
  MiniMap,
  Panel,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Node,
  NodeTypes,
  BackgroundVariant,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { clsx } from 'clsx'
import {
  SparklesIcon,
  UserGroupIcon,
  ArrowsPointingOutIcon,
  Square3Stack3DIcon,
  ArrowRightStartOnRectangleIcon,
  ArrowRightEndOnRectangleIcon,
  TrashIcon,
  PlayIcon,
  ArrowDownTrayIcon,
  Squares2X2Icon,
  CloudArrowUpIcon,
  FolderOpenIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
  CodeBracketIcon,
  DocumentTextIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline'
import AgentNodeComponent from '@/components/architect/AgentNode'
import NodeConfigPanel from '@/components/architect/NodeConfigPanel'
import {
  AgentNode,
  AgentNodeData,
  AgentEdge,
  PALETTE_ITEMS,
  WORKFLOW_TEMPLATES,
  WorkflowTemplate,
} from '@/types/architect'
import architectApi, {
  WorkflowSummary,
  CompileResult,
  ExecutionResult,
} from '@/services/architectApi'

// Custom node types
const nodeTypes: NodeTypes = {
  agentNode: AgentNodeComponent,
}

// Initial nodes for blank canvas
const initialNodes: AgentNode[] = []
const initialEdges: AgentEdge[] = []

const availableModels = ['gemini-3.1-flash-lite-preview', 'gemini-2.5-flash', 'gemini-2.5-pro', 'claude-sonnet-4', 'gpt-4o']

function AgentArchitectCanvas() {
  const reactFlowWrapper = useRef<HTMLDivElement>(null)
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)
  const [selectedNode, setSelectedNode] = useState<AgentNode | null>(null)
  const [showTemplates, setShowTemplates] = useState(false)
  const [registeredAgents, setRegisteredAgents] = useState<any[]>([])
  const [agentSearch, setAgentSearch] = useState('')

  // Fetch registered agents for the palette
  useEffect(() => {
    import('@/services/api').then(({ apiClient }) => {
      apiClient.getCentralizedAgents().then((agents: any[]) => {
        setRegisteredAgents(agents.filter((a: any) => a.enabled))
      }).catch(() => {})
    })
  }, [])

  // Workflow management state
  const [savedWorkflows, setSavedWorkflows] = useState<WorkflowSummary[]>([])
  const [currentWorkflowId, setCurrentWorkflowId] = useState<string | null>(null)
  const [workflowName, setWorkflowName] = useState('Untitled Workflow')
  const [executionEngine, setExecutionEngine] = useState<'langgraph' | 'unified' | 'hybrid'>('hybrid')

  // UI state
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [showLoadModal, setShowLoadModal] = useState(false)
  const [showExecuteModal, setShowExecuteModal] = useState(false)
  const [showResultsPanel, setShowResultsPanel] = useState(false)
  const [showCodePanel, setShowCodePanel] = useState(false)

  // Compile/Execute state
  const [compileResult, setCompileResult] = useState<CompileResult | null>(null)
  const [executionResult, setExecutionResult] = useState<ExecutionResult | null>(null)
  const [isCompiling, setIsCompiling] = useState(false)
  const [isExecuting, setIsExecuting] = useState(false)
  const [executePrompt, setExecutePrompt] = useState('')
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Load saved workflows on mount
  useEffect(() => {
    loadSavedWorkflows()
  }, [])

  const loadSavedWorkflows = async () => {
    try {
      // Fetch user-saved workflows from visual_workflows table
      const userWorkflows = await architectApi.listWorkflows(true, true).catch(() => [])

      // Fetch built-in workflow graphs from the unified endpoint
      let builtinWorkflows: WorkflowSummary[] = []
      try {
        const { apiClient } = await import('@/services/api')
        const resp = await (apiClient as any).client.get('/api/v1/workflows/graphs/summary')
        const summaries = resp.data?.workflows || []
        builtinWorkflows = summaries.map((s: any) => ({
          id: `builtin:${s.id}`,
          name: s.name,
          description: s.description || '',
          execution_engine: 'langgraph' as const,
          node_count: s.node_count || 0,
          is_template: true,
          tags: [s.category || 'Built-in'],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }))
      } catch (err) {
        console.error('Failed to fetch built-in workflows:', err)
      }

      // Combine: built-ins first, then user workflows
      setSavedWorkflows([...builtinWorkflows, ...userWorkflows])
    } catch (error) {
      console.error('Failed to load workflows:', error)
    }
  }

  // Handle new connections
  const onConnect = useCallback(
    (params: Connection) => {
      setEdges((eds: AgentEdge[]) => addEdge({ ...params, type: 'smoothstep', animated: true }, eds))
    },
    [setEdges]
  )

  // Handle node selection
  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNode(node as AgentNode)
  }, [])

  // Handle canvas click (deselect)
  const onPaneClick = useCallback(() => {
    setSelectedNode(null)
  }, [])

  // Update node data
  const updateNodeData = useCallback(
    (nodeId: string, data: Partial<AgentNodeData>) => {
      setNodes((nds: AgentNode[]) =>
        nds.map((node: AgentNode) =>
          node.id === nodeId
            ? { ...node, data: { ...node.data, ...data } }
            : node
        )
      )
    },
    [setNodes]
  )

  // Add node from palette
  const addNode = useCallback(
    (type: string, defaultData: Partial<AgentNodeData>) => {
      const newNode: AgentNode = {
        id: `${type}-${Date.now()}`,
        type: 'agentNode',
        position: { x: 250 + Math.random() * 200, y: 150 + Math.random() * 200 },
        data: {
          ...defaultData,
          label: defaultData.label || type,
          nodeType: defaultData.nodeType || 'specialist',
          isConfigured: defaultData.isConfigured || false,
        } as AgentNodeData,
      }
      setNodes((nds: AgentNode[]) => [...nds, newNode])
    },
    [setNodes]
  )

  // Load template
  const loadTemplate = useCallback(
    (template: WorkflowTemplate) => {
      const newNodes = template.nodes.map((n) => ({
        ...n,
        id: n.id || `node-${Date.now()}-${Math.random()}`,
        type: 'agentNode',
        data: n.data as AgentNodeData,
        position: n.position || { x: 100, y: 100 },
      })) as AgentNode[]

      const newEdges = template.edges.map((e) => ({
        ...e,
        id: e.id || `edge-${Date.now()}-${Math.random()}`,
        type: 'smoothstep',
        animated: true,
      })) as AgentEdge[]

      setNodes(newNodes)
      setEdges(newEdges)
      setWorkflowName(template.name)
      setShowTemplates(false)
      setCurrentWorkflowId(null)
      setCompileResult(null)
    },
    [setNodes, setEdges]
  )

  // Save workflow
  const saveWorkflow = async () => {
    try {
      if (currentWorkflowId) {
        await architectApi.updateWorkflow(currentWorkflowId, {
          name: workflowName,
          nodes,
          edges,
          execution_engine: executionEngine,
        })
        setStatusMessage({ type: 'success', text: 'Workflow updated!' })
      } else {
        const saved = await architectApi.createWorkflow({
          name: workflowName,
          nodes,
          edges,
          execution_engine: executionEngine,
        })
        setCurrentWorkflowId(saved.id)
        setStatusMessage({ type: 'success', text: 'Workflow saved!' })
      }
      setShowSaveModal(false)
      loadSavedWorkflows()
    } catch (error) {
      setStatusMessage({ type: 'error', text: 'Failed to save workflow' })
    }
    setTimeout(() => setStatusMessage(null), 3000)
  }

  // Load workflow
  const loadWorkflow = async (workflowId: string) => {
    try {
      // Built-in workflows use "builtin:" prefix and load from unified graphs endpoint
      if (workflowId.startsWith('builtin:')) {
        const graphId = workflowId.replace('builtin:', '')
        const { apiClient } = await import('@/services/api')
        const resp = await (apiClient as any).client.get(`/api/v1/workflows/graphs/${graphId}`)
        const graph = resp.data
        // Convert unified graph format to React Flow node/edge format
        const reactFlowNodes: AgentNode[] = (graph.nodes || []).map((n: any) => ({
          id: n.id,
          type: 'agentNode',
          position: n.position || { x: 0, y: 0 },
          data: {
            label: n.name,
            agentType: n.type,
            description: n.description,
            icon: n.icon,
            specialist_type: n.specialist_type,
            status: n.status || 'idle',
          },
        }))
        const reactFlowEdges: AgentEdge[] = (graph.edges || []).map((e: any, idx: number) => ({
          id: `e-${idx}`,
          source: e.source,
          target: e.target,
          label: e.label,
          type: 'smoothstep',
          animated: !e.is_conditional,
          data: {
            condition: e.condition,
            is_conditional: e.is_conditional,
          },
        }))
        setNodes(reactFlowNodes)
        setEdges(reactFlowEdges)
        setWorkflowName(graph.name || graphId)
        setExecutionEngine('langgraph')
        setCurrentWorkflowId(workflowId)
        setCompileResult(null)
        setShowLoadModal(false)
        setStatusMessage({ type: 'success', text: `Loaded built-in workflow: ${graph.name}` })
      } else {
        // User-saved workflow from visual_workflows table
        const workflow = await architectApi.getWorkflow(workflowId)
        setNodes(workflow.nodes as AgentNode[])
        setEdges(workflow.edges as AgentEdge[])
        setWorkflowName(workflow.name)
        setExecutionEngine(workflow.execution_engine as any)
        setCurrentWorkflowId(workflowId)
        setCompileResult(null)
        setShowLoadModal(false)
        setStatusMessage({ type: 'success', text: 'Workflow loaded!' })
      }
    } catch (error) {
      console.error('Load workflow error:', error)
      setStatusMessage({ type: 'error', text: 'Failed to load workflow' })
    }
    setTimeout(() => setStatusMessage(null), 3000)
  }

  // Compile workflow
  const compileWorkflow = async () => {
    setIsCompiling(true)
    try {
      const result = await architectApi.compileWorkflow({
        nodes,
        edges,
        execution_engine: executionEngine,
      })
      setCompileResult(result)
      if (result.is_valid) {
        setStatusMessage({ type: 'success', text: 'Workflow compiled successfully!' })
      } else {
        setStatusMessage({ type: 'error', text: `Validation errors: ${result.validation_errors.join(', ')}` })
      }
    } catch (error) {
      setStatusMessage({ type: 'error', text: 'Compilation failed' })
    }
    setIsCompiling(false)
    setTimeout(() => setStatusMessage(null), 5000)
  }

  // Execute workflow
  const executeWorkflow = async () => {
    if (!currentWorkflowId) {
      setStatusMessage({ type: 'error', text: 'Save workflow first before executing' })
      setTimeout(() => setStatusMessage(null), 3000)
      return
    }

    setIsExecuting(true)
    setShowExecuteModal(false)
    try {
      const result = await architectApi.executeWorkflow(currentWorkflowId, {
        prompt: executePrompt,
      })
      setExecutionResult(result)
      setShowResultsPanel(true)
      setStatusMessage({ type: 'success', text: 'Execution complete!' })
    } catch (error: any) {
      setStatusMessage({ type: 'error', text: error.response?.data?.detail || 'Execution failed' })
    }
    setIsExecuting(false)
    setTimeout(() => setStatusMessage(null), 5000)
  }

  // Export workflow
  const exportWorkflow = useCallback(() => {
    const workflow = {
      name: workflowName,
      nodes,
      edges,
      execution_engine: executionEngine,
      exportedAt: new Date().toISOString(),
    }
    const blob = new Blob([JSON.stringify(workflow, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${workflowName.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.json`
    a.click()
    URL.revokeObjectURL(url)
  }, [nodes, edges, workflowName, executionEngine])

  // Clear canvas
  const clearCanvas = useCallback(() => {
    if (confirm('Are you sure you want to clear the canvas?')) {
      setNodes([])
      setEdges([])
      setSelectedNode(null)
      setCurrentWorkflowId(null)
      setWorkflowName('Untitled Workflow')
      setCompileResult(null)
    }
  }, [setNodes, setEdges])

  // Palette icons mapping
  const paletteIcons: Record<string, React.ElementType> = {
    ArrowRightStartOnRectangleIcon,
    ArrowRightEndOnRectangleIcon,
    SparklesIcon,
    UserGroupIcon,
    ArrowsPointingOutIcon,
    Square3Stack3DIcon,
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* Main Canvas */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-2 bg-card border-b border-border">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowTemplates(!showTemplates)}
              className={clsx(
                'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors',
                showTemplates
                  ? 'bg-primary-600 text-white'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              )}
            >
              <Squares2X2Icon className="w-4 h-4" />
              Templates
            </button>
            <button
              onClick={() => setShowLoadModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-muted text-muted-foreground hover:bg-accent rounded-lg text-sm"
            >
              <FolderOpenIcon className="w-4 h-4" />
              Load
            </button>
            <button
              onClick={clearCanvas}
              className="flex items-center gap-2 px-3 py-1.5 bg-muted text-muted-foreground hover:bg-accent rounded-lg text-sm"
            >
              <TrashIcon className="w-4 h-4" />
              Clear
            </button>

            {/* Execution Engine Selector */}
            <select
              value={executionEngine}
              onChange={(e) => setExecutionEngine(e.target.value as any)}
              className="px-3 py-1.5 bg-muted text-muted-foreground rounded-lg text-sm border border-border"
            >
              <option value="hybrid">Hybrid (Recommended)</option>
              <option value="langgraph">LangGraph</option>
              <option value="unified">Unified</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSaveModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-muted text-muted-foreground hover:bg-accent rounded-lg text-sm"
            >
              <CloudArrowUpIcon className="w-4 h-4" />
              Save
            </button>
            <button
              onClick={exportWorkflow}
              className="flex items-center gap-2 px-3 py-1.5 bg-muted text-muted-foreground hover:bg-accent rounded-lg text-sm"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={compileWorkflow}
              disabled={isCompiling || nodes.length === 0}
              className={clsx(
                'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm',
                isCompiling || nodes.length === 0
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              )}
            >
              <CodeBracketIcon className="w-4 h-4" />
              {isCompiling ? 'Compiling...' : 'Compile'}
            </button>
            <button
              onClick={() => setShowExecuteModal(true)}
              disabled={isExecuting || !currentWorkflowId}
              className={clsx(
                'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm',
                isExecuting || !currentWorkflowId
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'bg-primary-600 hover:bg-primary-700 text-white'
              )}
            >
              <PlayIcon className="w-4 h-4" />
              {isExecuting ? 'Executing...' : 'Execute'}
            </button>
          </div>
        </div>

        {/* Status Message */}
        {statusMessage && (
          <div
            className={clsx(
              'px-4 py-2 text-sm flex items-center gap-2',
              statusMessage.type === 'success' ? 'bg-green-900/50 text-green-400' : 'bg-red-900/50 text-red-400'
            )}
          >
            {statusMessage.type === 'success' ? (
              <CheckCircleIcon className="w-4 h-4" />
            ) : (
              <ExclamationCircleIcon className="w-4 h-4" />
            )}
            {statusMessage.text}
          </div>
        )}

        {/* Template Selector */}
        {showTemplates && (
          <div className="px-4 py-3 bg-card/50 border-b border-border">
            <div className="text-sm text-muted-foreground mb-2">Choose a template to start:</div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {WORKFLOW_TEMPLATES.map((template) => (
                <button
                  key={template.id}
                  onClick={() => loadTemplate(template)}
                  className="flex-shrink-0 p-3 bg-card hover:bg-muted border border-border hover:border-primary-600 rounded-lg transition-colors"
                >
                  <div className="font-medium text-white text-sm">{template.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{template.description}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* React Flow Canvas */}
        <div ref={reactFlowWrapper} className="flex-1">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            defaultViewport={{ x: 0, y: 0, zoom: 0.7 }}
            minZoom={0.3}
            maxZoom={2}
            fitView
            fitViewOptions={{ padding: 0.3, maxZoom: 0.7 }}
            className="bg-background"
            defaultEdgeOptions={{ type: 'smoothstep', animated: true }}
          >
            <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#374151" />
            <Controls className="!bg-card !border-border !rounded-lg" />
            <MiniMap
              className="!bg-card !border-border !rounded-lg"
              nodeColor={(node: Node) => {
                const data = node.data as AgentNodeData
                if (data.nodeType === 'input') return '#22c55e'
                if (data.nodeType === 'output') return '#ef4444'
                if (data.nodeType === 'specialist') return '#8b5cf6'
                if (data.nodeType === 'supervisor') return '#a855f7'
                if (data.nodeType === 'router') return '#eab308'
                if (data.nodeType === 'aggregator') return '#3b82f6'
                return '#6b7280'
              }}
            />

            {/* Node Palette Panel */}
            <Panel position="top-left" className="!m-4">
              <div className="bg-card border border-border rounded-lg p-3 max-h-[70vh] overflow-y-auto" style={{ width: 220 }}>
                <div className="text-xs text-muted-foreground mb-2 font-medium">BUILDING BLOCKS</div>
                <div className="space-y-1.5">
                  {PALETTE_ITEMS.map((item) => {
                    const Icon = paletteIcons[item.icon] || SparklesIcon
                    return (
                      <button
                        key={item.type}
                        onClick={() => addNode(item.type, item.defaultData)}
                        className="flex items-center gap-2 w-full px-2.5 py-1.5 bg-background hover:bg-muted border border-border rounded-lg text-left transition-colors"
                        title={item.description}
                      >
                        <Icon className={clsx('w-4 h-4 shrink-0', `text-${item.color}-400`)} />
                        <span className="text-xs text-muted-foreground truncate">{item.label}</span>
                      </button>
                    )
                  })}
                </div>

                {/* Registered Agents */}
                {registeredAgents.length > 0 && (
                  <>
                    <div className="text-xs text-muted-foreground mt-3 mb-2 font-medium">REGISTERED AGENTS</div>
                    <input
                      type="text"
                      placeholder="Search agents..."
                      value={agentSearch}
                      onChange={(e) => setAgentSearch(e.target.value)}
                      className="w-full px-2 py-1 text-xs bg-background border border-border rounded mb-1.5 text-foreground placeholder-muted-foreground"
                    />
                    <div className="space-y-1">
                      {registeredAgents
                        .filter(a => !agentSearch || a.name?.toLowerCase().includes(agentSearch.toLowerCase()) || a.display_name?.toLowerCase().includes(agentSearch.toLowerCase()))
                        .slice(0, 15)
                        .map((agent) => (
                          <button
                            key={agent.name}
                            onClick={() => addNode('specialist', {
                              label: agent.display_name || agent.name,
                              nodeType: 'specialist',
                              specialistType: agent.name,
                              isConfigured: true,
                              agentName: agent.name,
                              domain: agent.domain,
                            })}
                            className="flex items-center gap-2 w-full px-2.5 py-1.5 bg-background hover:bg-violet-50 dark:hover:bg-violet-950/20 border border-border rounded-lg text-left transition-colors"
                            title={agent.description}
                          >
                            <SparklesIcon className="w-3.5 h-3.5 text-violet-400 shrink-0" />
                            <div className="min-w-0">
                              <span className="text-xs text-foreground truncate block">{agent.display_name || agent.name}</span>
                              <span className="text-[10px] text-muted-foreground truncate block">{agent.domain}</span>
                            </div>
                          </button>
                        ))
                      }
                    </div>
                  </>
                )}
              </div>
            </Panel>

            {/* Info Panel */}
            <Panel position="bottom-left" className="!m-4">
              <div className="bg-card/90 border border-border rounded-lg px-3 py-2 text-xs text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CpuChipIcon className="w-4 h-4" />
                  {nodes.length} nodes | {edges.length} connections
                  {compileResult?.is_valid && (
                    <span className="text-green-400 flex items-center gap-1">
                      <CheckCircleIcon className="w-3 h-3" /> Compiled
                    </span>
                  )}
                </div>
                {selectedNode && (
                  <div className="mt-1 text-primary-400">
                    Selected: {selectedNode.data.label}
                  </div>
                )}
              </div>
            </Panel>

            {/* Compile Result Panel */}
            {compileResult && compileResult.code && (
              <Panel position="bottom-right" className="!m-4">
                <button
                  onClick={() => setShowCodePanel(!showCodePanel)}
                  className="bg-card border border-border rounded-lg px-3 py-2 text-xs text-muted-foreground hover:bg-muted"
                >
                  <DocumentTextIcon className="w-4 h-4 inline mr-1" />
                  {showCodePanel ? 'Hide Code' : 'View Generated Code'}
                </button>
              </Panel>
            )}
          </ReactFlow>
        </div>
      </div>

      {/* Config Panel */}
      {selectedNode && (
        <NodeConfigPanel
          nodeId={selectedNode.id}
          data={selectedNode.data}
          onUpdate={updateNodeData}
          onClose={() => setSelectedNode(null)}
          availableModels={availableModels}
        />
      )}

      {/* Results Panel */}
      {showResultsPanel && executionResult && (
        <div className="w-96 bg-card border-l border-border flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h3 className="font-medium text-white">Execution Results</h3>
            <button onClick={() => setShowResultsPanel(false)} className="text-muted-foreground hover:text-white">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div>
              <div className="text-xs text-muted-foreground mb-1">Status</div>
              <div className={clsx(
                'text-sm',
                executionResult.status === 'completed' ? 'text-green-400' : 'text-red-400'
              )}>
                {executionResult.status}
              </div>
            </div>
            {executionResult.quality_score && (
              <div>
                <div className="text-xs text-muted-foreground mb-1">Quality Score</div>
                <div className="text-sm text-white">{executionResult.quality_score.toFixed(1)}/10</div>
              </div>
            )}
            {executionResult.duration_ms && (
              <div>
                <div className="text-xs text-muted-foreground mb-1">Duration</div>
                <div className="text-sm text-white">{(executionResult.duration_ms / 1000).toFixed(2)}s</div>
              </div>
            )}
            <div>
              <div className="text-xs text-muted-foreground mb-1">Output</div>
              <div className="text-sm text-muted-foreground bg-background rounded-lg p-3 max-h-96 overflow-y-auto whitespace-pre-wrap">
                {executionResult.final_output || 'No output'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Code Panel */}
      {showCodePanel && compileResult?.code && (
        <div className="w-[350px] bg-card border-l border-border flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h3 className="font-medium text-white">Generated Code</h3>
            <button onClick={() => setShowCodePanel(false)} className="text-muted-foreground hover:text-white">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <pre className="p-4 text-xs text-muted-foreground font-mono">{compileResult.code}</pre>
          </div>
        </div>
      )}

      {/* Save Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 w-96">
            <h3 className="text-lg font-medium text-white mb-4">Save Workflow</h3>
            <input
              type="text"
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-white mb-4"
              placeholder="Workflow name"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowSaveModal(false)}
                className="px-4 py-2 bg-muted text-muted-foreground rounded-lg hover:bg-accent"
              >
                Cancel
              </button>
              <button
                onClick={saveWorkflow}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load Modal */}
      {showLoadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 w-[350px] max-h-[600px] overflow-y-auto">
            <h3 className="text-lg font-medium text-white mb-4">Load Workflow</h3>
            {savedWorkflows.length === 0 ? (
              <p className="text-muted-foreground">No saved workflows</p>
            ) : (
              <div className="space-y-2">
                {savedWorkflows.map((wf) => (
                  <button
                    key={wf.id}
                    onClick={() => loadWorkflow(wf.id)}
                    className="w-full p-3 bg-background hover:bg-muted rounded-lg text-left"
                  >
                    <div className="text-white font-medium">{wf.name}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {wf.node_count} nodes | {wf.execution_engine} | {new Date(wf.updated_at).toLocaleDateString()}
                    </div>
                  </button>
                ))}
              </div>
            )}
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowLoadModal(false)}
                className="px-4 py-2 bg-muted text-muted-foreground rounded-lg hover:bg-accent"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Execute Modal */}
      {showExecuteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 w-[350px]">
            <h3 className="text-lg font-medium text-white mb-4">Execute Workflow</h3>
            <div className="mb-4">
              <label className="block text-sm text-muted-foreground mb-2">Enter your prompt:</label>
              <textarea
                value={executePrompt}
                onChange={(e) => setExecutePrompt(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-white h-32 resize-none"
                placeholder="Describe what you want the workflow to do..."
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowExecuteModal(false)}
                className="px-4 py-2 bg-muted text-muted-foreground rounded-lg hover:bg-accent"
              >
                Cancel
              </button>
              <button
                onClick={executeWorkflow}
                disabled={!executePrompt.trim()}
                className={clsx(
                  'px-4 py-2 rounded-lg',
                  executePrompt.trim()
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-muted text-muted-foreground cursor-not-allowed'
                )}
              >
                Execute
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default function AgentArchitectPage() {
  return (
    <div className="-m-4 lg:-m-6 h-[calc(100vh-3.5rem)] flex flex-col">
      <div className="px-4 py-2 border-b border-border flex items-center justify-between shrink-0">
        <div>
          <p className="text-sm text-muted-foreground">
            Visually design agent workflows with LangGraph/Unified hybrid execution
          </p>
        </div>
      </div>

      <div className="flex-1 min-h-0">
        <ReactFlowProvider>
          <AgentArchitectCanvas />
        </ReactFlowProvider>
      </div>
    </div>
  )
}
